import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BlogLayout from '../../components/blog/BlogLayout';
import BlogContent from '../../components/blog/BlogContent';
import BlogMeta from '../../components/blog/BlogMeta';
import useBlogPost from '../../hooks/useBlogPost';

export default function BlogPostPage() {
  const { slug } = useParams();
  const { post, loading, error } = useBlogPost(slug);

  useEffect(() => {
    if (post?.title) {
      document.title = `${post.title} | SureDox Blog`;
    }
    return () => {
      document.title = 'SureDox';
    };
  }, [post?.title]);

  return (
    <BlogLayout>
      <article className="mx-auto max-w-4xl px-6 py-12">
        {loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Link
              to="/blog"
              className="text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              Back to Blog
            </Link>
          </div>
        )}

        {post && (
          <>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <header className="mb-8">
              <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white mb-4">
                {post.title}
              </h1>
              <BlogMeta post={post} />
            </header>

            {post.featuredImage && (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full rounded-lg mb-8"
              />
            )}

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="px-2.5 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            <BlogContent content={post.body} />
          </>
        )}
      </article>
    </BlogLayout>
  );
}
